import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CartProvider, useCart } from './context/CartContext';
import Header from './components/Header';
import TopBar from './components/TopBar';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import ChatBot from './components/ChatBot';
import Home from './pages/Home';
import Shop from './pages/Shop';
import Contact from './pages/Contact';
import CatalogoUnas from './pages/CatalogoUnas';
import GuiaPedido from './pages/GuiaPedido';
import { SITE } from './config/site';
import useRealtimeSync from './hooks/useRealtimeSync';
import useCartReserva from './hooks/useCartReserva';
import usePrefetchCatalogImages from './hooks/usePrefetchCatalogImages';

function Toast({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="toast" onClick={onClose}>{message}</div>
  );
}

function AppShell() {
  const location = useLocation();
  const [cartOpen, setCartOpen] = useState(false);
  const [toast, setToast] = useState('');
  const { dispatch, totalItems, state: cartState } = useCart();
  useCartReserva(cartState.items, {
    canal: 'web',
    onAdjusted: (adjusted) => {
      setToast(`Stock ajustado (reserva 30 min): ${adjusted.map((a) => a.codigo).join(', ')}`);
    },
  });
  usePrefetchCatalogImages({ enabled: true });

  const getToken = useCallback(() => '', []);
  // No recargar catálogo entero al reconectar SSE: cancelaba la 1ª carga y dejaba "Cargando…" eterno.
  const onRealtimeReconnect = useCallback(() => {}, []);

  // SSE de tienda pública OFF por defecto: cada visitante abría una conexión larga al API (LVE).
  // Activar solo si hace falta: REACT_APP_TIENDA_SSE=1 en el build.
  const sseOn =
    process.env.REACT_APP_TIENDA_SSE === '1' && /^\/catalogo/.test(location.pathname || '');
  useRealtimeSync({
    app: 'tienda',
    enabled: sseOn,
    getToken,
    onReconnect: onRealtimeReconnect,
  });
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const onInv = (ev) => {
      const detail = ev.detail || {};
      if (detail.reason !== 'producto_eliminado' || !detail.codigo) return;
      dispatch({ type: 'REMOVE_BY_CODIGO', codigo: detail.codigo });
      setToast(`Se quitó ${detail.codigo} del carrito (ya no está en catálogo)`);
    };
    window.addEventListener('oh:invalidate', onInv);
    return () => window.removeEventListener('oh:invalidate', onInv);
  }, [dispatch]);

  function addProduct(product, cantidadOrOpts = 1) {
    const opts =
      cantidadOrOpts && typeof cantidadOrOpts === 'object'
        ? cantidadOrOpts
        : { cantidad: cantidadOrOpts };
    dispatch({
      type: 'ADD',
      product,
      cantidad: opts.cantidad ?? 1,
      observaciones: opts.observaciones || '',
      opcion_id: opts.opcion_id || null,
      opcion_nombre: opts.opcion_nombre || null,
      imagen: opts.imagen || null,
    });
    setToast('Agregado al carrito');
  }

  return (
    <div className="store-app">
      <div className="store-chrome">
        <TopBar />
        <Header onOpenCart={() => setCartOpen(true)} />
      </div>
      <main className="store-main">
        <Routes>
          <Route path="/" element={<Home onAdd={addProduct} onToast={setToast} />} />
          <Route path="/catalogo" element={<Shop onAdd={addProduct} onToast={setToast} />} />
          <Route path="/tienda" element={<Navigate to="/catalogo" replace />} />
          <Route path="/catalogo-unas" element={<CatalogoUnas onAdd={addProduct} onToast={setToast} />} />
          <Route path="/como-comprar" element={<GuiaPedido />} />
          <Route path="/contacto" element={<Contact />} />
        </Routes>
      </main>
      <Footer />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <Toast message={toast} onClose={() => setToast('')} />

      <ChatBot />
      <button
        type="button"
        className={`cart-fab${totalItems > 0 ? ' has-items' : ''}`}
        onClick={() => setCartOpen(true)}
        aria-label={`Carrito${totalItems ? `, ${totalItems} productos` : ''}`}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
          <path d="M6 6h15l-1.5 9h-12z" />
          <path d="M6 6l-1-3H2" />
          <circle cx="9" cy="20" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="17" cy="20" r="1.4" fill="currentColor" stroke="none" />
        </svg>
        {totalItems > 0 ? <span className="cart-fab-count">{totalItems}</span> : null}
      </button>
      <a
        href={`https://wa.me/${SITE.whatsapp}`}
        className="whatsapp-float"
        target="_blank"
        rel="noreferrer"
        aria-label="WhatsApp"
      >
        <span>WhatsApp</span>
      </a>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <CartProvider>
        <AppShell />
      </CartProvider>
    </BrowserRouter>
  );
}
